import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../useAuth';
import type {
  CommentNode,
  CreateReplyPayload,
  CreateRootCommentPayload,
  UpdateCommentPayload,
} from '../../domains/comments/models/Comment';

interface CommentResponse {
  comment: CommentNode;
}

type CreateRootCommentVariables = {
  diagramId: string;
  input: CreateRootCommentPayload;
};

type ReplyToCommentVariables = {
  diagramId: string;
  parentId: string;
  input: CreateReplyPayload;
};

type UpdateCommentVariables = {
  commentId: string;
  input: UpdateCommentPayload;
};

type CommentIdVariables = {
  commentId: string;
};

async function ensureCommentResponse(response: Response, errorMessage: string): Promise<CommentNode> {
  if (!response.ok) {
    throw new Error(errorMessage);
  }
  const data: CommentResponse = await response.json();
  return data.comment;
}

function jsonRequestInit(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
}

type AuthenticatedFetch = (url: string, options?: RequestInit) => Promise<Response>;

function shouldUseLinkFallback(response: Response): boolean {
  return response.status === 401 || response.status === 403;
}

export async function createRootCommentRequest(
  authenticatedFetch: AuthenticatedFetch,
  diagramId: string,
  input: CreateRootCommentPayload,
): Promise<CommentNode> {
  let response = await authenticatedFetch(
    `/api/diagrams/${diagramId}/comments`,
    jsonRequestInit(input),
  );
  if (shouldUseLinkFallback(response)) {
    response = await fetch(`/api/link/diagrams/${diagramId}/comments`, {
      ...jsonRequestInit(input),
      credentials: 'include',
    });
  }
  return ensureCommentResponse(response, 'Failed to create comment');
}

export async function replyToCommentRequest(
  authenticatedFetch: AuthenticatedFetch,
  diagramId: string,
  parentId: string,
  input: CreateReplyPayload,
): Promise<CommentNode> {
  let response = await authenticatedFetch(
    `/api/comments/${parentId}/replies`,
    jsonRequestInit(input),
  );
  if (shouldUseLinkFallback(response)) {
    response = await fetch(`/api/link/diagrams/${diagramId}/comments/${parentId}/replies`, {
      ...jsonRequestInit(input),
      credentials: 'include',
    });
  }
  return ensureCommentResponse(response, 'Failed to create reply');
}

export function useCreateRootCommentMutation(
  options?: UseMutationOptions<CommentNode, Error, CreateRootCommentVariables>,
): UseMutationResult<CommentNode, Error, CreateRootCommentVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<CommentNode, Error, CreateRootCommentVariables>({
    mutationFn: async ({ diagramId, input }) => {
      return createRootCommentRequest(authenticatedFetch, diagramId, input);
    },
    ...options,
  });
}

export function useReplyToCommentMutation(
  options?: UseMutationOptions<CommentNode, Error, ReplyToCommentVariables>,
): UseMutationResult<CommentNode, Error, ReplyToCommentVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<CommentNode, Error, ReplyToCommentVariables>({
    mutationFn: async ({ diagramId, parentId, input }) => {
      return replyToCommentRequest(authenticatedFetch, diagramId, parentId, input);
    },
    ...options,
  });
}

export function useUpdateCommentMutation(
  options?: UseMutationOptions<CommentNode, Error, UpdateCommentVariables>,
): UseMutationResult<CommentNode, Error, UpdateCommentVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<CommentNode, Error, UpdateCommentVariables>({
    mutationFn: async ({ commentId, input }) => {
      const response = await authenticatedFetch(`/api/comments/${commentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });
      return ensureCommentResponse(response, 'Failed to update comment');
    },
    ...options,
  });
}

export function useResolveCommentMutation(
  options?: UseMutationOptions<CommentNode, Error, CommentIdVariables>,
): UseMutationResult<CommentNode, Error, CommentIdVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<CommentNode, Error, CommentIdVariables>({
    mutationFn: async ({ commentId }) => {
      const response = await authenticatedFetch(`/api/comments/${commentId}/resolve`, {
        method: 'POST',
      });
      return ensureCommentResponse(response, 'Failed to resolve comment');
    },
    ...options,
  });
}

export function useReopenCommentMutation(
  options?: UseMutationOptions<CommentNode, Error, CommentIdVariables>,
): UseMutationResult<CommentNode, Error, CommentIdVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<CommentNode, Error, CommentIdVariables>({
    mutationFn: async ({ commentId }) => {
      const response = await authenticatedFetch(`/api/comments/${commentId}/reopen`, {
        method: 'POST',
      });
      return ensureCommentResponse(response, 'Failed to reopen comment');
    },
    ...options,
  });
}

export function useSoftDeleteCommentMutation(
  options?: UseMutationOptions<void, Error, CommentIdVariables>,
): UseMutationResult<void, Error, CommentIdVariables> {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation<void, Error, CommentIdVariables>({
    mutationFn: async ({ commentId }) => {
      const response = await authenticatedFetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }
    },
    ...options,
  });
}
